"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { NAV_ITEMS, FRESHA_GIFT_VOUCHER_URL } from "@/lib/constants";

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
}

type MobileView = "main" | "treatments" | "concerns";

export function MobileNav({ open, onClose }: MobileNavProps) {
  const [view, setView] = useState<MobileView>("main");
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Body scroll lock while open.
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Dialog contract: move focus into the panel on open, trap Tab inside
  // it, close on Escape, and restore focus to the trigger on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setView("main");
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  function handleClose() {
    setView("main");
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Ink scrim */}
      <div
        className="fixed inset-0 bg-ink/60"
        onClick={handleClose}
      />

      {/* Off-canvas panel from the right: ivory field, gold top hairline */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className="fixed right-0 top-0 bottom-0 w-[85vw] max-w-md bg-ivory flex flex-col"
        style={{ animation: "slideInRight 320ms var(--ease-silk-out, ease-out) both" }}
      >
        <div aria-hidden="true" className="h-px bg-gold flex-shrink-0" />
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-16 border-b silk-hairline flex-shrink-0">
          {view !== "main" ? (
            <button
              type="button"
              className="flex items-center gap-2 text-sm font-medium text-gold-deep"
              onClick={() => setView("main")}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              Back
            </button>
          ) : (
            <Link href="/" onClick={handleClose} className="flex flex-col items-stretch flex-shrink-0 leading-none">
              <span className="font-display text-xl uppercase whitespace-nowrap text-ink" style={{ fontWeight: 450 }}>
                Silk Clinical
              </span>
              <span
                aria-hidden="true"
                className="flex justify-between text-[9px] uppercase mt-0.5 text-muted"
              >
                {"AESTHETICS".split("").map((ch, i) => (
                  <span key={i}>{ch}</span>
                ))}
              </span>
              <span className="sr-only">Aesthetics</span>
            </Link>
          )}
          <button
            ref={closeButtonRef}
            type="button"
            className="p-2 -mr-2 text-charcoal"
            onClick={handleClose}
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-hidden relative">
          {/* Main menu */}
          <div
            className={`absolute inset-0 flex flex-col transition-transform duration-250 ease-in-out ${
              view === "main" ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <nav aria-label="Mobile" className="flex-1 px-5 py-6">
              <ul className="space-y-1">
                {NAV_ITEMS.map((item) => {
                  if ("menu" in item) {
                    return (
                      <li key={item.label}>
                        <button
                          type="button"
                          className="silk-mobile-nav-link w-full flex items-center justify-between py-3"
                          onClick={() => setView(item.menu)}
                        >
                          {item.label}
                          <svg className="w-5 h-5 text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                        </button>
                      </li>
                    );
                  }

                  if ("external" in item && item.external) {
                    return (
                      <li key={item.label}>
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="silk-mobile-nav-link block py-3"
                          onClick={handleClose}
                        >
                          {item.label}
                        </a>
                      </li>
                    );
                  }

                  return (
                    <li key={item.label}>
                      <Link
                        href={item.href}
                        className="silk-mobile-nav-link block py-3"
                        onClick={handleClose}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Treatments submenu */}
          <div
            className={`absolute inset-0 flex flex-col transition-transform duration-250 ease-in-out overflow-y-auto ${
              view === "treatments" ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="px-5 py-6">
              <Link
                href="/treatments"
                className="block py-2 text-sm font-medium text-gold-deep mb-2"
                onClick={handleClose}
              >
                View All Treatments
              </Link>

              {NAV_ITEMS.filter((item) => "children" in item && item.children).map((item) =>
                "children" in item && item.children
                  ? item.children.map((category) => (
                      <div key={category.category} className="mt-5">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted mb-2">
                          {category.category}
                        </p>
                        {category.items.map((subItem) => (
                          <Link
                            key={subItem.href}
                            href={subItem.href}
                            className="block py-2.5 text-sm text-ink active:text-gold-deep"
                            onClick={handleClose}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    ))
                  : null
              )}
            </div>
          </div>

          {/* Your Concern submenu: same slide-in pattern, flat list of the
              by-need concerns. */}
          <div
            className={`absolute inset-0 flex flex-col transition-transform duration-250 ease-in-out overflow-y-auto ${
              view === "concerns" ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="px-5 py-6">
              <Link
                href="/concerns"
                className="block py-2 text-sm font-medium text-gold-deep mb-2"
                onClick={handleClose}
              >
                Explore by concern
              </Link>

              {NAV_ITEMS.filter((item) => "concerns" in item).map((item) =>
                "concerns" in item
                  ? item.concerns.map((concern) => (
                      <Link
                        key={concern.href}
                        href={concern.href}
                        className="block py-2.5 text-sm text-ink active:text-gold-deep"
                        onClick={handleClose}
                      >
                        {concern.label}
                      </Link>
                    ))
                  : null
              )}
            </div>
          </div>
        </div>

        {/* Bottom section. No Book Now here: it lives in the persistent
            BookingCTA bar docked to the bottom of the mobile viewport, so
            the burger panel stays purely navigation plus reach-me links. */}
        <div className="flex-shrink-0 px-5 py-5 border-t border-border space-y-4 safe-area-bottom">
          {/* Secondary links: moved out of the primary nav, kept reachable */}
          <div className="flex items-center gap-5 text-[13px]">
            <Link
              href="/blog"
              className="text-muted active:text-gold-deep"
              onClick={handleClose}
            >
              Journal
            </Link>
            <span aria-hidden="true" className="text-border">
              ·
            </span>
            <a
              href={FRESHA_GIFT_VOUCHER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted active:text-gold-deep"
              onClick={handleClose}
            >
              Gift Vouchers
            </a>
          </div>
          <div className="flex items-center justify-between text-sm text-muted">
            <a href="tel:033655774" className="active:text-gold-deep">
              03 365 5774
            </a>
            <a href="mailto:bookings@silkclinical.co.nz" className="active:text-gold-deep">
              bookings@silkclinical.co.nz
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
